import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Accordion,
    Button,
    ButtonGroup,
    Card,
    Col,
    Container,
    Dropdown,
    Image,
    ListGroup,
    OverlayTrigger,
    Popover,
    Row
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import { IoEllipsisHorizontal } from "react-icons/io5";
import {
    getAllReportIntervention,
    getAllReportByGroup,
    getAllReportByUser,
    downloadPdf,
    getAllReport,
    getReportsCount
} from "../api/reportApi";
import { format } from "date-fns";
import fr from "date-fns/locale/fr";
import CustomLoader from "../components/LoaderComponent";
import { customHeader } from "../utils/StyledDataTable";
import { FormModal } from "../components/FormModal";
import { AuthContext } from "../context/AuthContext";
import {
    DisplayNoteProps,
    DisplayReasonProps,
    DisplayStatusProps,
    GroupEntry,
    ReportInterface,
    ReportIntervention,
    RowActionsProps,
    DisplayGroupProps,
    UserGroupInterface
} from "../utils/Type";
import FilterComponent from "../components/FilterComponent";
import { TableContainer } from "../utils/StyledComponents";
import viewReport from "../asset/img/icon/view.png";
import pdfReport from "../asset/img/icon/pdf.png";
import { LuFilter } from "react-icons/lu";
import { toast } from "react-toastify";
import { getAllGroup, getGroup, getMsgGroup } from "../api/appliApi";
import { GrGroup } from "react-icons/gr";

const REASONS: { [key: string]: string } = {
    DEFECTIVE: "Matériel défectueux",
    CUSTOM_INTERVENTION: "Le client a rejeté l'installation",
    PROBLEM: "Problème avec la date et l'heure de l'intervention",
    INTERNET: "Pas d'internet",
    NOT_AVAILABLE: "Le client n'était pas présent ou disponible",
    FAILURE_NOT_OBSERVED: "Panne non constatée",
    CLOSED_SITE: "Site fermé",
    DUPLICATE_INTERVENTION: "Doublon intervention",
    PARCEL_DELIVERY: "Livraison colis",
    OTHER: "Autre (voir commentaire)"
};

const STATUS_LABELS: { [key: string]: string } = {
    "": "Tous",
    NEW: "Nouveau",
    DRAFT: "Brouillon",
    IN_PROGRESS: "En cours",
    COMPLETED: "Complété"
};

const STATUS_CONFIG: Record<string, { label: string; bg: string }> = {
    NEW: { label: "Nouveau", bg: "#E5E7EB" },
    DRAFT: { label: "Brouillon", bg: "#FDE68A" },
    IN_PROGRESS: { label: "En cours", bg: "#BFDBFE" },
    COMPLETED: { label: "Complété", bg: "#A7F3D0" }
};

const getNoteConfig = (note: number): { label: string; bg: string } => {
    if (note >= 9 && note <= 10) return { label: `${note}`, bg: "#6EE7B7" };
    if (note >= 7 && note <= 8) return { label: `${note}`, bg: "#FDBA74" };
    if (note >= 1 && note <= 6) return { label: `${note}`, bg: "#FCA5A5" };
    if (note < 0) return { label: "N/D", bg: "#CCCCCC" };
    return { label: "N/A", bg: "#CCCCCC" };
};

const DisplayReason: React.FC<DisplayReasonProps> = ({ reasonKey }) => {
    return <>{REASONS[reasonKey]}</>;
};

const DisplayStatusInline: React.FC<DisplayStatusProps> = ({ statusKey }) => {
    return <>{STATUS_LABELS[statusKey]}</>;
};

const DisplayStatusTable: React.FC<DisplayStatusProps> = ({ statusKey }) => {
    const { label, bg } = STATUS_CONFIG[statusKey] || { label: statusKey || "–", bg: "#CCCCCC" };
    return (
        <span
            style={{
                display: "flex",
                backgroundColor: bg,
                color: "#000",
                borderRadius: "12px",
                padding: "4px 8px",
                fontWeight: 500,
                textAlign: "center",
                width: "75px",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            {label}
        </span>
    );
};

const DisplayNoteTable: React.FC<DisplayNoteProps> = ({ note }) => {
    const { label, bg } = getNoteConfig(note);
    return (
        <span
            style={{
                display: "flex",
                backgroundColor: bg,
                color: "#000",
                borderRadius: "12px",
                padding: "4px 8px",
                fontWeight: 500,
                textAlign: "center",
                width: "55px",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            {label}
        </span>
    );
};

const DisplayGroupName: React.FC<DisplayGroupProps> = ({ name }) => {
    return (
        <span
            style={{
                display: "inline-flex",
                backgroundColor: "#EEE3FF",
                color: "#000",
                borderRadius: "12px",
                padding: "4px 8px",
                fontWeight: 500,
                textAlign: "center",
                width: "auto",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            {name}
        </span>
    );
};

export function RowActions({ onView, onDownloadPdf }: RowActionsProps) {
    const popover = (
        <Popover id="popover-report" className="dropdown-menu">
            <Popover.Body className="p-0 border-0">
                <ul className="list-unstyled mb-0">
                    <li>
                        <button
                            className="dropdown-item"
                            onClick={(e) => { e.stopPropagation(); onView(); }}
                        >
                            <img src={viewReport} width={21} alt="voir le rapport" className="me-2" /> Voir le détail du rapport
                        </button>
                    </li>
                    <div className="dropdown-divider" />
                    <li>
                        <button
                            className="dropdown-item"
                            onClick={(e) => { e.stopPropagation(); onDownloadPdf(); }}
                        >
                            <img src={pdfReport} width={21} alt="pdf du rapport" className="me-2" /> Télécharger le rapport
                        </button>
                    </li>
                </ul>
            </Popover.Body>
        </Popover>
    );

    return (
        <OverlayTrigger trigger="click" placement="bottom" overlay={popover} rootClose container={document.body}>
            <Button variant="light" size="sm" className="p-0 border-0">
                <IoEllipsisHorizontal size={20} />
            </Button>
        </OverlayTrigger>
    );
}

const Report: React.FC = () => {
    const [filterText, setFilterText] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [filteredData, setFilteredData] = useState<ReportInterface[]>([]);
    const [selectedId, setSelectedId] = useState("");
    const [pending, setPending] = useState(true);
    const [modalShow, setModalShow] = useState(false);
    const [reportIntervention, setReportIntervention] = useState<ReportIntervention | null>(null);
    const auth = useContext(AuthContext);
    const [size, setSize] = useState(30);
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [showSondageLabel, setShowSondageLabel] = useState<string | number>();
    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
    const [groupData, setGroupData] = useState<GroupEntry[]>([]);
    const [serialNumber, setSerialNumber] = useState("");
    const [groupNameData, setGroupNameData] = useState<UserGroupInterface[]>([]);
    const [groupFilter, setGroupFilter] = useState<string | "">("");

    const [pagesCache, setPagesCache] = useState<ReportInterface[][]>([]);
    const [pageIndex, setPageIndex] = useState<number>(0);
    const [isFetchingPage, setIsFetchingPage] = useState(false);
    const [pageHasNext, setPageHasNext] = useState<boolean[]>([]);
    const pageSizeDefault = size ?? 30;
    const [totalPages, setTotalPages] = useState<number | null>(0);

    const [appliedFilters, setAppliedFilters] = useState({
        keyword: "",
        serialNumber: "",
        status: "",
        startDate: undefined as string | undefined,
        endDate: undefined as string | undefined,
        groupId: "" as string | ""
    });

    const fetchReport = async (
        authParam: any,
        cursor?: { lastCreatedAt?: string; lastId?: string },
        sizeParam = pageSizeDefault,
        appendToCache = true
    ) => {
        setIsFetchingPage(true);
        try {
            let response: any;
            if (authParam?.role === "admin") {
                const storedGroupes = localStorage.getItem("groupes") ?? "[]";
                const groupesIds: number[] = JSON.parse(storedGroupes);
                setSelectedGroupIds(groupesIds);
                response = await getAllReportByGroup({
                    ids: groupesIds.map(String),
                    lastCreatedAt: cursor?.lastCreatedAt,
                    lastId: cursor?.lastId,
                    size: sizeParam,
                    keyword: appliedFilters.keyword,
                    serialNumber: appliedFilters.serialNumber,
                    status: appliedFilters.status,
                    startDate: appliedFilters.startDate,
                    endDate: appliedFilters.endDate,
                    groupId: appliedFilters.groupId ? Number(appliedFilters.groupId) : undefined
                });
            } else if (authParam?.role === "technician") {
                response = await getAllReportByUser({
                    id: authParam?.userId,
                    lastCreatedAt: cursor?.lastCreatedAt,
                    lastId: cursor?.lastId,
                    size: sizeParam,
                    keyword: appliedFilters.keyword,
                    serialNumber: appliedFilters.serialNumber,
                    status: appliedFilters.status,
                    startDate: appliedFilters.startDate,
                    endDate: appliedFilters.endDate,
                    groupId: appliedFilters.groupId ? Number(appliedFilters.groupId) : undefined
                });
            } else {
                response = await getAllReport({
                    lastCreatedAt: cursor?.lastCreatedAt,
                    lastId: cursor?.lastId,
                    size: sizeParam,
                    keyword: appliedFilters.keyword,
                    serialNumber: appliedFilters.serialNumber,
                    status: appliedFilters.status,
                    startDate: appliedFilters.startDate,
                    endDate: appliedFilters.endDate,
                    groupId: appliedFilters.groupId ? Number(appliedFilters.groupId) : undefined
                });
            }

            const content: ReportInterface[] = Array.isArray(response?.content) ? response.content : [];
            const hasNextFromBackend: boolean | undefined = typeof response?.hasNext === 'boolean' ? response.hasNext : undefined;

            if (appendToCache) {
                setPagesCache(prev => {
                    const newPages = [...prev, content];
                    const newIndex = prev.length;
                    setPageIndex(newIndex);
                    return newPages;
                });
                setPageHasNext(prev => {
                    const newArr = [...prev, typeof hasNextFromBackend === "boolean" ? hasNextFromBackend : content.length >= sizeParam];
                    return newArr;
                });
            } else {
                setPagesCache(prev => {
                    const newPages = [...prev];
                    if (pageIndex < 0 || pageIndex >= newPages.length) {
                        newPages.push(content);
                        setPageIndex(newPages.length - 1);
                    } else {
                        newPages[pageIndex] = content;
                    }
                    return newPages;
                });
                setPageHasNext(prev => {
                    const newArr = [...prev];
                    newArr[pageIndex] = typeof hasNextFromBackend === "boolean" ? hasNextFromBackend : content.length >= sizeParam;
                    return newArr;
                });
            }


            setFilteredData(content);
        } catch (err) {
            console.error("fetchPage error", err);
            setFilteredData([]);
        } finally {
            setIsFetchingPage(false);
        }
    };

    useEffect(() => {
        setPagesCache([]);
        setPageHasNext([]);
        setPageIndex(0);
        setFilteredData([]);
        (async () => {
            setPending(true);
            try {
                await fetchReport(auth, undefined, size);
            } finally {
                setPending(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, appliedFilters, size]);

    useEffect(() => {
        if (!selectedGroupIds.length) return;

        setGroupData([]);
        selectedGroupIds.forEach(async (gid) => {
            try {
                const { data } = await getMsgGroup(String(gid));
                const entry: GroupEntry = { id: data.id, isActivatedSondage: data.isActivatedSondage };
                setGroupData(prev => [...prev, entry]);
            } catch (err) {
                setGroupData(prev => [...prev, { id: -1, isActivatedSondage: false }]);
            }
        });
    }, [selectedGroupIds]);

    useEffect(() => {
        if (!reportIntervention) return;
        if (auth?.role !== "superadmin" && groupData.length === 0) return;

        const rep = reportIntervention.report;
        let label: string | number = rep.sondage ?? 0;
        if (label < 0) label = "non demandé";
        else if (label === 0) label = "non renseigné";
        setShowSondageLabel(label);
    }, [reportIntervention, groupData, auth]);

    const fetchGroupData = async () => {
        try {
            const data = await getAllGroup();
            if (Array.isArray(data?.data)) {
                const normalized: UserGroupInterface[] = data.data;
                setGroupNameData([{ id: "", name: "Tout", codeName: "TOUT" } as any, ...normalized]);
            } else {
                setGroupNameData([{ id: "", name: "Tout", codeName: "TOUT" } as any]);
            }
        } catch (error) {
            setGroupNameData([{ id: "", name: "Tout", codeName: "TOUT" } as any]);
        }
    };

    const fetchGroupeUser = async () => {
        const storedGroupes = localStorage.getItem("groupes");
        const groupesIds: number[] = storedGroupes ? JSON.parse(storedGroupes) : [];
        if (groupesIds.length === 0) {
            setGroupNameData([{ id: "", name: "Tout", codeName: "TOUT" } as any]);
            return;
        }
        const data = await getGroup(groupesIds.map(id => id.toString()));
        if (Array.isArray(data?.data)) {
            const normalized: UserGroupInterface[] = data.data;
            setGroupNameData([{ id: "", name: "Tout", codeName: "TOUT" } as any, ...normalized]);
        }
    }

    useEffect(() => {
        if (auth?.role === "superadmin") {
            fetchGroupData();
        } else {
            fetchGroupeUser();
        }
    }, [auth]);

    useEffect(() => {
        let mounted = true;
        const fetchCount = async () => {
            try {
                const count = await getReportsCount({
                    keyword: appliedFilters.keyword,
                    serialNumber: appliedFilters.serialNumber,
                    status: appliedFilters.status,
                    startDate: appliedFilters.startDate,
                    endDate: appliedFilters.endDate,
                    groupId: appliedFilters.groupId ? Number(appliedFilters.groupId) : undefined
                });
                if (!mounted) return;
                setTotalPages(Math.max(1, Math.ceil(count / size)));
            } catch (err) {
                console.error("count error", err);
                if (mounted) setTotalPages(null);
            }
        };
        fetchCount();
        return () => { mounted = false; }
    }, [appliedFilters, size]);


    const handleClear = () => {
        setFilterText("");
        setStartDate(undefined);
        setEndDate(undefined);
        setStatusFilter("");
        setAppliedFilters({
            keyword: "",
            serialNumber: "",
            status: "",
            startDate: undefined,
            endDate: ""
        } as any);
        setSerialNumber("");
        setGroupFilter("");
    };

    const handleNext = async () => {
        if (pageIndex < pagesCache.length - 1) {
            const nextIndex = pageIndex + 1;
            setPageIndex(nextIndex);
            setFilteredData(pagesCache[nextIndex] ?? []);
            return;
        }

        const currentPage = pagesCache[pageIndex] ?? filteredData;
        if (!currentPage || currentPage.length === 0) return;
        const lastItem = currentPage[currentPage.length - 1];
        if (!lastItem) return;

        const cursor = { lastCreatedAt: lastItem.created_at, lastId: lastItem.id };
        await fetchReport(auth, cursor, size, true);
    };

    const handlePrev = () => {
        if (pageIndex === 0) return;
        const prevIndex = pageIndex - 1;
        setPageIndex(prevIndex);
        setFilteredData(pagesCache[prevIndex] ?? []);
    };

    const handleSearch = () => {
        setAppliedFilters({
            keyword: filterText,
            serialNumber: serialNumber,
            status: statusFilter,
            startDate,
            endDate,
            groupId: groupFilter
        });
    };

    const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterText(e.target.value);
    };

    const baseColumns = useMemo(() => {
        return [
            {
                name: "N° d'intervention",
                selector: (row: ReportInterface) => row.external_case_code,
                sortable: false
            },
            {
                name: "Nom du client",
                selector: (row: ReportInterface) => row.onsite_client_name,
                sortable: false
            },
            {
                name: "ID commerçant",
                selector: (row: ReportInterface) => row.external_merchant_id,
                sortable: false
            },
            {
                name: "Status",
                cell: (row: ReportInterface) => <DisplayStatusTable statusKey={row.status} />,
                sortable: false,
                width: "10%"
            },
            {
                name: "Rattacht",
                cell: (row: any) => (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: row.groupCodeName !== row.userGroupCodeName ? "#6930C3" : "transparent",
                                marginRight: 8,
                                flexShrink: 0
                            }}
                        />
                        <div title={row.groupName}>
                            <DisplayGroupName name={row.groupCodeName} />
                        </div>
                    </div>
                ),
                sortable: false,
                width: "8%"
            },
            {
                name: "Technicien",
                cell: (row: any) => (
                    <div style={{ display: "flex", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: "8px" }}>
                            {row.userName ?? "–"}
                        </span>
                        <div title={row.userGroupName}>
                            <DisplayGroupName name={row.userGroupCodeName ?? "–"} />
                        </div>
                    </div>
                ),
                sortable: false,
                width: "20%"
            }
        ];
    }, []);

    const sondageColumn = useMemo(() => {
        return {
            name: "Sondage",
            cell: (row: ReportInterface) => <DisplayNoteTable note={row.sondage} />,
            sortable: false,
            width: "7%"
        };
    }, []);

    const endColumns = useMemo(() => {
        return [
            {
                name: "Date de création",
                selector: (row: ReportInterface) => {
                    const dateStr = row.created_at?.trim();
                    if (!dateStr) return "–";
                    const date = new Date(dateStr);
                    return format(date, "dd/MM/yyyy HH:mm", { locale: fr });
                },
                sortable: false,
                width: "9%"
            },
            {
                name: "",
                cell: (row: ReportInterface) => (
                    <RowActions
                        onView={() => handleShow(row)}
                        onDownloadPdf={() => handleDownloadPDF(row.id)} />
                ),
                sortable: false,
                width: "4%"
            }
        ];
    }, []);

    const columns = useMemo(() => {
        return [
            ...baseColumns,
            ...(auth?.role === "admin" || auth?.role === "superadmin" ? [sondageColumn] : []),
            ...endColumns
        ];
    }, [baseColumns, sondageColumn, endColumns, auth]);

    const conditionalRowStyles = useMemo(() => {
        return [
            {
                when: (row: ReportInterface) => filteredData.indexOf(row) % 2 !== 0,
                style: { backgroundColor: "#F6F8FD" }
            }
        ];
    }, [filteredData]);

    const handleShow = async (item: any) => {
        try {
            // setModalPending(true);
            const response = await getAllReportIntervention(item.id);
            setReportIntervention(response.data);
            setSelectedId(response.data.report.id);
            setModalShow(true);
        } catch (error) {
            console.error("handleShow error", error);
            setReportIntervention(null);
            setModalShow(true);
        } finally {
            // setModalPending(false);
        }
    };

    const handleDownloadPDF = async (id: string) => {
        try {
            document.body.style.cursor = "wait";
            await downloadPdf(id);
        } catch (error: any) {
            toast.error("Erreur lors du téléchargement du PDF: " + (error?.message ?? String(error)));
        } finally {
            document.body.style.cursor = "default";
        }
    };

    return (
        <div className="custom-content">
            <div className="custom-header">
                <span className="custom-title">Liste des rapports</span>
            </div>

            <div className="mb-3 d-flex" style={{ position: "relative", zIndex: 2, marginBottom: "20px" }}>
                <FilterComponent filterText={filterText} onFilter={handleFilter} btnClear={false} />

                <Dropdown as={ButtonGroup} className="me-2" drop="down">
                    <Dropdown.Toggle id="dropdown-filter-status" variant="light" className="report-filter">
                        <LuFilter size={18} style={{ marginRight: "6px" }} />
                        {statusFilter ? <DisplayStatusInline statusKey={statusFilter} /> : "Filtrer"}
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ minWidth: "200px", paddingTop: "4px", paddingBottom: "4px" }}>
                        {Object.entries(STATUS_LABELS).map(([key, label]) => (
                            <Dropdown.Item
                                key={key}
                                active={statusFilter === key}
                                onClick={() => setStatusFilter(key)}
                                style={{
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    ...(statusFilter === key
                                        ? { backgroundColor: "#6930C3", color: "#fff" }
                                        : { color: "#333" })
                                }}
                            >
                                {label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>

                </Dropdown>

                <Dropdown as={ButtonGroup} className="me-2" drop="down">
                    <Dropdown.Toggle
                        id="dropdown-filter-group"
                        variant="light"
                        className="report-filter"
                    >
                        <GrGroup style={{ marginRight: "6px" }} size={18} />
                        <span className="report-filter-label">
                            {groupFilter !== ""
                                ? groupNameData.find((g) => g.id === groupFilter)?.name ?? String(groupFilter)
                                : "Groupe"}
                        </span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ minWidth: "220px", paddingTop: "4px", paddingBottom: "4px" }}>
                        {groupNameData.length === 0 ? (
                            <Dropdown.Item disabled style={{ padding: "8px 12px" }}>
                                Aucun groupe
                            </Dropdown.Item>
                        ) : (
                            groupNameData.map((group) => (
                                <Dropdown.Item
                                    key={String(group.id)}
                                    active={groupFilter === group.id}
                                    onClick={() => setGroupFilter((group.id as any) ?? "")}
                                    style={{
                                        padding: "10px 14px",
                                        width: "100%",
                                        display: "block",
                                        borderRadius: "4px",
                                        ...(groupFilter === group.id
                                            ? { backgroundColor: "#6930C3", color: "#fff" }
                                            : { color: "#333" }),
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: "8px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            width: "100%",
                                        }}
                                    >
                                        <span
                                            style={{
                                                flex: 1,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                            title={group.name}
                                        >
                                            {group.name}
                                        </span>

                                        {group.codeName && (
                                            <span
                                                style={{ flexShrink: 0, marginLeft: "8px" }}
                                                title={group.codeName}
                                            >
                                                <DisplayGroupName name={group.codeName} />
                                            </span>
                                        )}
                                    </div>
                                </Dropdown.Item>
                            ))
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                <div className="me-3">
                    <input type="date" className="custom-date" value={startDate ?? ""} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="me-3">
                    <input type="date" className="custom-date" value={endDate ?? ""} onChange={e => setEndDate(e.target.value)} />
                </div>
                <div className="me-3">
                    <input type="text" className="custom-input-log" style={{ width: "300px" }} placeholder="Numéro de série" value={serialNumber} onChange={e => setSerialNumber(e.target.value)} />
                </div>
            </div>

            <div className="mb-3 d-flex" style={{ position: "relative", marginTop: "-32px" }}>
                <button onClick={handleSearch} className="btn-add-circle" style={{ height: "42px", marginRight: "8px" }}>
                    Appliquer
                </button>

                <Button variant="secondary" onClick={handleClear} style={{ height: "42px" }}>
                    Réinitialiser
                </Button>
            </div>

            <TableContainer>
                <DataTable
                    columns={columns as any}
                    data={filteredData}
                    pagination={false}
                    progressPending={pending || isFetchingPage}
                    progressComponent={<CustomLoader />}
                    customStyles={customHeader}
                    conditionalRowStyles={conditionalRowStyles as any}
                    persistTableHead
                    noDataComponent={<p style={{ marginTop: "20px" }}>Aucun enregistrement trouvé.</p>}
                />

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "16px",
                        padding: "16px 0",
                        borderTop: "1px solid #e0e0e0",
                        marginTop: "8px",
                    }}
                >
                    {/* Bouton Précédent */}
                    {pageIndex === 0 || isFetchingPage ?
                        null : (<Button
                            variant="secondary"
                            size="sm"
                            onClick={handlePrev}
                            disabled={pageIndex === 0 || isFetchingPage}
                        >
                            ← Précédent
                        </Button>)}


                    <div style={{ color: "#444", fontWeight: 500 }}>
                        Page {pageIndex + 1}{totalPages ? ` / ${totalPages}` : " / ?"}
                    </div>

                    {isFetchingPage ||
                        (typeof pageHasNext[pageIndex] === "boolean"
                            ? !pageHasNext[pageIndex]
                            : (pagesCache[pageIndex]?.length ?? filteredData.length) < size) ?
                        null : (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleNext}
                                disabled={
                                    isFetchingPage ||
                                    (typeof pageHasNext[pageIndex] === "boolean"
                                        ? !pageHasNext[pageIndex]
                                        : (pagesCache[pageIndex]?.length ?? filteredData.length) < size)
                                }
                            >
                                Suivant →
                            </Button>
                        )}

                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <label htmlFor="pageSizeSelect" style={{ fontSize: "0.9rem", color: "#555" }}>
                            Lignes par page :
                        </label>
                        <select
                            id="pageSizeSelect"
                            value={size}
                            onChange={(e) => {
                                const newSize = parseInt(e.target.value, 10);
                                setSize(newSize);
                                setPagesCache([]);
                                setPageHasNext([]);
                                setPageIndex(0);
                                setFilteredData([]);
                            }}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "6px",
                                padding: "4px 8px",
                                fontSize: "0.9rem",
                                backgroundColor: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            <option value={30}>30</option>
                            <option value={60}>60</option>
                            <option value={90}>90</option>
                        </select>
                    </div>
                </div>

            </TableContainer>

            <FormModal
                show={modalShow}
                size="xl"
                btnValidate="Télécharger le PDF"
                btnCancel="Fermer"
                onValidate={() => handleDownloadPDF(selectedId)}
                onAbort={() => setModalShow(false)}
                title={`Rapport ${reportIntervention?.report.isMailSend ? `` : "(e-mail non envoyé)"}`}
                body={
                    <div id="reportModalContent">
                        <Card>
                            <ListGroup>
                                <ListGroup.Item>Numéro du rapport : {reportIntervention?.report.id}</ListGroup.Item>
                                <ListGroup.Item>Référence demande d'intervention : {reportIntervention?.report.external_case_code}</ListGroup.Item>
                                <ListGroup.Item>Référence Commerçant : {reportIntervention?.report.external_merchant_id}</ListGroup.Item>
                            </ListGroup>
                        </Card>
                        <br />
                        <Container>
                            <Row>
                                <Col md={7}>
                                    <Card>
                                        <Card.Header className="text-center">Information générales</Card.Header>
                                        <ListGroup variant="flush">
                                            <ListGroup.Item>
                                                Date : {reportIntervention?.report.created_at ? format(new Date(reportIntervention.report.created_at), "dd/MM/yyyy HH:mm", { locale: fr }) : "Date non disponible"}
                                            </ListGroup.Item>
                                            <ListGroup.Item>Technicien : {reportIntervention?.report.userName}</ListGroup.Item>
                                            <ListGroup.Item>Statut : {reportIntervention?.report.status ? <DisplayStatusInline statusKey={reportIntervention?.report.status} /> : <></>}</ListGroup.Item>
                                            <ListGroup.Item>L'intervention a-t-elle eu lieu ? : {reportIntervention?.report.case_carried_out ? "Oui" : "Non"}</ListGroup.Item>
                                            <ListGroup.Item>Le technicien s'est-il déplacé sur site ? : {reportIntervention?.report.technicians_traveled_site ? "Oui" : "Non"}</ListGroup.Item>
                                            {reportIntervention?.report.comments && (<ListGroup.Item>Commentaire client : {reportIntervention.report.comments}</ListGroup.Item>)}
                                            {showSondageLabel && (<ListGroup.Item>Satisfaction NPS : {showSondageLabel}</ListGroup.Item>)}
                                            {reportIntervention?.report.technician_comments && (<ListGroup.Item>Commentaire technicien : {reportIntervention.report.technician_comments}</ListGroup.Item>)}
                                            {reportIntervention?.report.case_not_done_reason && (<ListGroup.Item>Pourquoi l'intervention n'a-t-elle pas eu lieu ? : <DisplayReason reasonKey={reportIntervention.report.case_not_done_reason} /></ListGroup.Item>)}
                                            <ListGroup.Item>Un paiement test a-t-il été réalisé avec succès ? : {reportIntervention?.report.is_test_payment_done ? "Oui" : "Non"}</ListGroup.Item>
                                            <ListGroup.Item>Une vérification de la télécollecte a-t-elle été effectuée avec succès ? : {reportIntervention?.report.is_teleccollect_done ? "Oui" : "Non"}</ListGroup.Item>
                                            <ListGroup.Item>Une vérification des domiciliations a-t-elle été effectuée avec succès ? : {reportIntervention?.report.is_domiciliations_done ? "Oui" : "Non"}</ListGroup.Item>
                                        </ListGroup>
                                    </Card>
                                </Col>
                                <Col md={5}>
                                    <Card>
                                        <Card.Header className="text-center">Client</Card.Header>
                                        <ListGroup variant="flush">
                                            {reportIntervention?.report.onsite_client_name && (<ListGroup.Item>Interlocuteur sur site : {reportIntervention.report.onsite_client_name}</ListGroup.Item>)}
                                            {reportIntervention?.report.onsite_service_client_address && (<ListGroup.Item>Adresse service sur site : {reportIntervention.report.onsite_service_client_address}</ListGroup.Item>)}
                                            {reportIntervention?.report.onsite_client_contact_phone && (<ListGroup.Item>Téléphone de contact fourni : {reportIntervention.report.onsite_client_contact_phone}</ListGroup.Item>)}
                                            {reportIntervention?.report.onsite_client_email && (<ListGroup.Item>Courriel de contact fourni : {reportIntervention.report.onsite_client_email}</ListGroup.Item>)}
                                        </ListGroup>
                                    </Card>
                                </Col>
                            </Row>
                        </Container>
                        <br />
                        <Accordion>
                            {(reportIntervention?.interventions ?? []).map((intervention, index) => (
                                <Accordion.Item eventKey={String(index)} key={index}>
                                    <Accordion.Header>{intervention.category}</Accordion.Header>
                                    <Accordion.Body>
                                        <ListGroup variant="flush">
                                            {intervention.device && (<ListGroup.Item>Type de matériel : {intervention.device}</ListGroup.Item>)}
                                            {intervention.bankingDomiciliation && (<ListGroup.Item>Domiciliation bancaire : {intervention.bankingDomiciliation}</ListGroup.Item>)}
                                            {intervention.device_id && (<ListGroup.Item>Modèle : {intervention.deviceIdName}</ListGroup.Item>)}
                                            {intervention.base_id && (<ListGroup.Item>Model de la Base : {intervention.baseIdName}</ListGroup.Item>)}
                                            {intervention.pinpad_id && (<ListGroup.Item>Pinpad : {intervention.pinpadIdName}</ListGroup.Item>)}
                                            {intervention.device_serial_number && (<ListGroup.Item>Numéro de série : {intervention.device_serial_number}</ListGroup.Item>)}
                                            {intervention.category === "SWAP" && intervention.old_device_serial_number && (<ListGroup.Item>Numéro de série de l'ancien terminal : {intervention.old_device_serial_number}</ListGroup.Item>)}

                                            {intervention.device === "BASE" || intervention.device === "PINPAD" || intervention.device === "CHECK_READER" || intervention.device === "VITAL" ? (<></>) : (<ListGroup.Item>Une base est-elle connectée au terminal? : {intervention.is_device_has_base_installed ? "Oui" : "Non"}</ListGroup.Item>)}
                                            {intervention.device_base_id && (<ListGroup.Item>Modèle de la base : {intervention.deviceBaseIdName}</ListGroup.Item>)}
                                            {intervention.device_base_serial_number && (<ListGroup.Item>Numéro de série de la base : {intervention.device_base_serial_number}</ListGroup.Item>)}
                                            {intervention.old_device_base_serial_number && (<ListGroup.Item>Numéro de série de l’ancienne Base : {intervention.old_device_base_serial_number}</ListGroup.Item>)}

                                            {intervention.device === "BASE" || intervention.device === "PINPAD" || intervention.device === "CHECK_READER" || intervention.device === "VITAL" ? (<></>) : (<ListGroup.Item>Un Pinpad est-il connecté au terminal? : {intervention.is_device_has_pinpad_installed ? "Oui" : "Non"}</ListGroup.Item>)}
                                            {intervention.device_pinpad_id && (<ListGroup.Item>Reference du PinPad : {intervention.devicePinpadIdName}</ListGroup.Item>)}
                                            {intervention.device_pinpad_serial_number && (<ListGroup.Item>Numéro de série du PinPad : {intervention.device_pinpad_serial_number}</ListGroup.Item>)}
                                            {intervention.old_device_pinpad_serial_number && (<ListGroup.Item>Numéro de série de l’ancien Pinpad : {intervention.old_device_pinpad_serial_number}</ListGroup.Item>)}

                                            {intervention.device === "BASE" || intervention.device === "PINPAD" || intervention.device === "CHECK_READER" || intervention.device === "VITAL" ? (<></>) : (<ListGroup.Item>Un lecteur de chèque est-il installé ? : {intervention.is_device_has_check_reader_installed ? "Oui" : "Non"}</ListGroup.Item>)}
                                            {intervention.device_check_reader_serial_number && (<ListGroup.Item>Numéro de série du lecteur de chèque : {intervention.device_check_reader_serial_number}</ListGroup.Item>)}
                                            {intervention.device_check_reader_connection_type && (<ListGroup.Item>Type de connexion du lecteur de chèque : {intervention.device_check_reader_connection_type}</ListGroup.Item>)}
                                            {intervention.old_device_check_reader_serial_number && (<ListGroup.Item>Numéro de série de l’ancien lecteur chèque : {intervention.old_device_check_reader_serial_number}</ListGroup.Item>)}

                                            {intervention.category === "REPAIR" && intervention.error_code && (<ListGroup.Item>Code erreur : {intervention.errorCodeName}</ListGroup.Item>)}
                                            {intervention.category === "REPAIR" && intervention.solution_code && (<ListGroup.Item>Code depannage : {intervention.solutionCodeName}</ListGroup.Item>)}

                                            {intervention.device === "BASE" || intervention.device === "PINPAD" || intervention.device === "CHECK_READER" || intervention.device === "VITAL" ? (<></>) : (<ListGroup.Item>Composants additionnel : {intervention.nonSerialPartsIdsName?.length > 0 ? intervention.nonSerialPartsIdsName.join(", ") : "Aucun donnée"}</ListGroup.Item>)}

                                            {intervention.category === "REPAIR" && (<ListGroup.Item>La SIM a-t-elle été remplacé ? : {intervention.is_sim_replaced ? "Oui" : "Non"}</ListGroup.Item>)}
                                            {intervention.main_sim_id && (<ListGroup.Item>Type de SIM : {intervention.mainSimName}</ListGroup.Item>)}
                                            {intervention.category === "REPAIR" && intervention.main_sim_number && (<ListGroup.Item>Numéro de SIM : {intervention.main_sim_number}</ListGroup.Item>)}
                                            {intervention.replaced_sim_id && (<ListGroup.Item>SIM replacée : {intervention.replacedSimName}</ListGroup.Item>)}

                                            {intervention.category === "REPAIR" && (<ListGroup.Item>La configuration a-t-elle été changée ? : {intervention.is_configuration_changed ? "Oui" : "Non"}</ListGroup.Item>)}
                                            {(intervention.device === "BASE" || intervention.device === "PINPAD" || intervention.device === "CHECK_READER") && intervention.connected_terminal_serial_number && (<ListGroup.Item>Numero de série du terminal connecté : {intervention.connected_terminal_serial_number}</ListGroup.Item>)}

                                            {intervention.device_primary_connection_type && (<ListGroup.Item>Type de connexion terminal primaire : {intervention.device_primary_connection_type}</ListGroup.Item>)}
                                            {intervention.main_ip_config_type && (<ListGroup.Item>Type de configuration IP Primaire : {intervention.main_ip_config_type}</ListGroup.Item>)}

                                            {intervention.main_ip_config_ip && (<ListGroup.Item>Adresse IP : {intervention.main_ip_config_ip}</ListGroup.Item>)}
                                            {intervention.main_ip_config_gateway && (<ListGroup.Item>Adresse Gateway : {intervention.main_ip_config_gateway}</ListGroup.Item>)}
                                            {intervention.main_ip_config_dns1 && (<ListGroup.Item>Adresse dns1 : {intervention.main_ip_config_dns1}</ListGroup.Item>)}
                                            {intervention.main_ip_config_dns2 && (<ListGroup.Item>Adresse dns2 : {intervention.main_ip_config_dns2}</ListGroup.Item>)}

                                            {intervention.device === "BASE" || intervention.device === "PINPAD" || intervention.device === "CHECK_READER" || intervention.device === "VITAL" || intervention.is_configuration_changed === false ? (<></>) : (<ListGroup.Item>Un back-up a-t-il été configuré? : {intervention.is_device_has_backup_configured ? "Oui" : "Non"}</ListGroup.Item>)}
                                            {intervention.backup_type && (<ListGroup.Item>Type de backup : {intervention.backup_type}</ListGroup.Item>)}
                                            {intervention.main_sim_number && (<ListGroup.Item>Numéro de série de la SIM : {intervention.main_sim_number}</ListGroup.Item>)}
                                            {intervention.backup_sim_id && (<ListGroup.Item>Type de SIM du backup : {intervention.backupSimName}</ListGroup.Item>)}
                                            {intervention.backup_sim_number && (<ListGroup.Item>Numéro de SIM du backup : {intervention.backup_sim_number}</ListGroup.Item>)}
                                            {intervention.backup_ip_config_type && (<ListGroup.Item>Type de configuration IP du backup : {intervention.backup_ip_config_type}</ListGroup.Item>)}
                                            {intervention.backup_ip_config_ip && (<ListGroup.Item>Adresse IP du backup : {intervention.backup_ip_config_ip}</ListGroup.Item>)}
                                            {intervention.backup_ip_config_gateway && (<ListGroup.Item>Adresse Gateway du backup : {intervention.backup_ip_config_gateway}</ListGroup.Item>)}
                                            {intervention.backup_ip_config_dns1 && (<ListGroup.Item>Adresse dns1 du backup : {intervention.backup_ip_config_dns1}</ListGroup.Item>)}
                                            {intervention.backup_ip_config_dns2 && (<ListGroup.Item>Adresse dns2 du backup : {intervention.backup_ip_config_dns2}</ListGroup.Item>)}

                                            {intervention.device === "BASE" || intervention.device === "PINPAD" || intervention.device === "CHECK_READER" || intervention.device === "VITAL" ? (<></>) : (<ListGroup.Item>Nom des applications monétiques : {intervention.applicationIdsName?.length > 0 ? intervention.applicationIdsName.join(", ") : "Aucun donnée"}</ListGroup.Item>)}
                                            {intervention.bridge_id && (<ListGroup.Item>Nom de la passerelle monétique : {intervention.bridgeIdName}</ListGroup.Item>)}

                                            {intervention.device === "BASE" || intervention.device === "PINPAD" || intervention.device === "CHECK_READER" || intervention.device === "VITAL" ? (<></>) : (<ListGroup.Item>Une caisse est-elle connectée au terminal ? : {intervention.is_device_connected_cash_system ? "Oui" : "Non"}</ListGroup.Item>)}
                                            {intervention.device_cash_system_connection_type && (<ListGroup.Item>Via quel type de connexion ? : {intervention.device_cash_system_connection_type}</ListGroup.Item>)}

                                            {(intervention.category === "SWAP" || intervention.category === "PICKUP") && intervention.device === "TERMINAL" && (<ListGroup.Item>Ce terminal nécessite-t-il une vérification ou une télécollecte ? : {intervention.is_device_need_telecollect_or_verification ? "Oui" : "Non"}</ListGroup.Item>)}

                                            <ListGroup.Item>
                                                Photos :
                                                {(intervention.photos ?? []).map((photo: string, i: number) => (
                                                    <Image src={photo} key={i} alt={`Photo ${i}`} width={300} height={300} style={{ transform: "rotate(90deg)", transformOrigin: "center center" }} />
                                                ))}
                                            </ListGroup.Item>

                                            {(intervention.category === "PICKUP" || intervention.category === "SWAP") && (
                                                <ListGroup.Item>
                                                    Materiel récupéré :
                                                    {(intervention.old_photos ?? []).map((photo: string, i: number) => (
                                                        <Image src={photo} key={i} alt={`Photo ${i}`} width={300} height={300} style={{ transform: "rotate(90deg)", transformOrigin: "center center" }} />
                                                    ))}
                                                </ListGroup.Item>
                                            )}
                                        </ListGroup>
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>

                        <br />
                        {(reportIntervention?.report?.global_photos ?? []).length > 0 && (
                            <Card>
                                <ListGroup.Item>
                                    Photos global :
                                    {(reportIntervention?.report?.global_photos ?? []).map((photo: string, i: number) => (
                                        <Image src={photo} key={i} alt={`Photo ${i}`} width={300} height={300} style={{ transform: "rotate(90deg)", transformOrigin: "center center" }} />
                                    ))}
                                </ListGroup.Item>
                            </Card>
                        )}
                    </div>
                }
            />
        </div>
    );
};

export default Report;
